import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/layout/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav
          className={`flex items-center justify-between rounded-full px-4 sm:px-6 py-3 transition-all duration-500 ${
            scrolled ? "glass-heavy" : "glass"
          }`}
        >
          <Logo />
          <div className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "text-white bg-white/5"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="navbar-cta-button"
              onClick={() => navigate("/contact#book")}
              className="hidden sm:inline-flex items-center gap-1.5 bg-white text-black hover:bg-zinc-100 transition-all px-4 py-2 rounded-full text-sm font-semibold"
            >
              Book a Call <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-full hover:bg-white/5 transition"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden mt-3 glass-heavy rounded-3xl p-4 flex flex-col gap-1"
          >
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-4 py-3 rounded-xl text-zinc-300 hover:bg-white/5 hover:text-white text-sm font-medium"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 px-4 py-3 rounded-xl bg-white text-black text-center text-sm font-semibold"
            >
              Book a Strategy Call
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
